import type { LucideIcon } from "@/lib/icons";
import {
  Atom,
  Table as TableIcon,
  Link2,
  Scale,
  Wind,
  Droplet,
  FlaskConical,
  Flame,
  Radiation,
  ShieldCheck,
} from "@/lib/icons";

export type PathwayLevel = "Foundation" | "Intermediate";

export interface ProjectPathway {
  level: PathwayLevel;
  title: string;
  description: string;
}

export interface ProjectToolLink {
  label: string;
  path: string;
}

export interface ProjectTopic {
  id: string;
  number: number;
  title: string;
  teks: string;
  objective: string;
  icon: LucideIcon;
  pathways: ProjectPathway[];
  tools: ProjectToolLink[];
}

export interface TeacherResources {
  /** A 1-2 sentence hook to open the lesson with. */
  lessonStarter: string;
  /** Step-by-step teacher instructions for running the topic. */
  instructions: string[];
  /** Notes on suggested ElementPad tools and how to use them. */
  toolNotes: string[];
}

export const PROJECT_TOPICS: ProjectTopic[] = [
  {
    id: "lab-foundations",
    number: 0,
    title: "Lab Foundations: Safety, Methods & Measurement",
    teks: "§112.35(c)(1)-(3), (4)",
    objective:
      "Establish the scientific process strands every Texas chemistry student is expected to use all year: lab safety and SDS literacy, scientific methods, critical thinking, units & significant figures, and graphing.",
    icon: ShieldCheck,
    pathways: [
      {
        level: "Foundation",
        title: "Safety Contract & Measurement Toolkit",
        description:
          "Read 2-3 SDS sheets and summarize hazards, PPE, and first aid; pass a 20-question safety quiz; complete a measurement worksheet covering SI units, unit conversion, significant figures, accuracy vs. precision, and one graphing exercise.",
      },
      {
        level: "Intermediate",
        title: "Mini Inquiry Investigation",
        description:
          "Design and run a short open inquiry (e.g., 'Which brand of paper towel absorbs the most water?'). Write a hypothesis, identify variables, collect data with correct units and sig figs, graph the result, and produce an error analysis.",
      },
    ],
    tools: [
      { label: "Worksheet Generator", path: "/worksheet" },
      { label: "Online Lab", path: "/lab" },
    ],
  },
  {
    id: "periodic-table",
    number: 1,
    title: "Periodic Table & Elemental Trends",
    teks: "§112.35(c)(6)",
    objective:
      "Investigate the organization of the periodic table and discover patterns in electronegativity, atomic radius, ionization energy, and reactivity.",
    icon: TableIcon,
    pathways: [
      {
        level: "Foundation",
        title: "Research & Analysis",
        description:
          "Create a poster or digital presentation analyzing 4-6 elements from different families (alkali metals, halogens, noble gases, alkaline earth, transition). Include symbols, atomic numbers, electron configurations, key properties, and trend graphs.",
      },
      {
        level: "Intermediate",
        title: "Trend Prediction & Anomaly Analysis",
        description:
          "Pick an 'unknown' element by position only and predict its radius, ionization energy, electronegativity, and reactivity. Graph one trend across a period AND down a group, then explain at least one anomaly (e.g., O vs. N ionization energy, or the d-block contraction).",
      },
    ],
    tools: [
      { label: "Periodic Table", path: "/" },
      { label: "Periodic Sprint", path: "/play/periodic-sprint" },
      { label: "Element of the Day", path: "/daily" },
    ],
  },
  {
    id: "atomic-structure",
    number: 2,
    title: "Atomic Structure & Quantum Mechanics",
    teks: "§112.35(c)(5)(B)-(C), (7)(A)-(B)",
    objective:
      "Trace the evolution of atomic models from Dalton to modern quantum mechanics, then apply it to electron configurations and bonding.",
    icon: Atom,
    pathways: [
      {
        level: "Foundation",
        title: "Historical Development",
        description:
          "Build an illustrated timeline (poster, slides, or infographic) of atomic models from Dalton to Heisenberg. Include scientist, date, key discoveries, limitations, and visuals.",
      },
      {
        level: "Intermediate",
        title: "Model Construction & Experimentation",
        description:
          "Build physical or digital models of 4-6 atoms/ions showing electron configurations. Conduct flame tests or emission spectroscopy and document the link between transitions and wavelengths.",
      },
    ],
    tools: [
      { label: "3D Atom Viewer", path: "/atom" },
      { label: "Electron Config Sprint", path: "/play/electron-config-sprint" },
      { label: "Online Lab (flame tests)", path: "/lab" },
    ],
  },
  {
    id: "bonding",
    number: 3,
    title: "Chemical Bonding & Molecular Geometry",
    teks: "§112.35(c)(7)(C)-(E), (8)",
    objective:
      "Investigate ionic, covalent, and metallic bonding, predict molecular geometry with VSEPR, and connect intramolecular bonds AND intermolecular forces to physical/chemical properties.",
    icon: Link2,
    pathways: [
      {
        level: "Foundation",
        title: "Bonding Classification Study",
        description:
          "Analyze 8-10 compounds: bonding type, electron-dot structures, predicted geometry, and properties (solubility, melting point, conductivity). Present in charts, diagrams, and tables.",
      },
      {
        level: "Intermediate",
        title: "Property Testing & IMF Mystery",
        description:
          "Test 5-7 compounds for solubility, melting/boiling points, and conductivity. Then solve a 'boiling-point mystery': rank 4-5 small molecules (e.g., H2O, H2S, CH4, NH3, HF) and justify using London, dipole-dipole, and hydrogen bonding. Include crystal-structure or IMF diagrams.",
      },
    ],
    tools: [
      { label: "Ion Engine", path: "/ions" },
      { label: "Bond Type Snap", path: "/play/bond-type-snap" },
      { label: "Molecule Viewer", path: "/tools/molecule-viewer" },
    ],
  },
  {
    id: "stoichiometry",
    number: 4,
    title: "Stoichiometry & Chemical Reactions",
    teks: "§112.35(c)(9)-(10)",
    objective:
      "Apply stoichiometric calculations to real reactions and use quantitative data to determine chemical relationships.",
    icon: Scale,
    pathways: [
      {
        level: "Foundation",
        title: "Calculation Practice & Case Studies",
        description:
          "Compile 15-20 solved problems covering mole conversions, mass relationships, gas volumes, limiting reactants, and percent yield. Include flowcharts/concept maps linking mass, moles, particles, and volume.",
      },
      {
        level: "Intermediate",
        title: "Quantitative Reaction Lab",
        description:
          "Run a wet or virtual reaction (e.g., baking soda + vinegar gas yield, or the copper cycle Cu -> Cu(NO3)2 -> Cu(OH)2 -> CuO -> CuSO4 -> Cu). Predict theoretical yield, measure actual yield, identify the limiting reactant, and calculate percent yield with an error analysis.",
      },
    ],
    tools: [
      { label: "Equation Balancer", path: "/tools/equation-balancer" },
      { label: "Molar Mass Calculator", path: "/tools/molar-mass" },
      { label: "Reaction Race", path: "/play/reaction-race" },
    ],
  },
  {
    id: "gas-laws",
    number: 5,
    title: "Gas Laws & Kinetic Molecular Theory",
    teks: "§112.35(c)(11)",
    objective:
      "Investigate gas behavior experimentally and apply the combined and ideal gas laws, Dalton's Law of partial pressures, and KMT principles.",
    icon: Wind,
    pathways: [
      {
        level: "Foundation",
        title: "Gas Laws Study & Calculations",
        description:
          "Build a study guide on Boyle's, Charles's, Gay-Lussac's, the Combined Gas Law, the Ideal Gas Law (PV=nRT), and Dalton's Law of partial pressures. Include theory, math, real-world uses, and solved problems via infographics and concept maps.",
      },
      {
        level: "Intermediate",
        title: "Gas Behavior Experimentation",
        description:
          "Design and conduct 3-4 experiments on pressure-volume, volume-temperature, or gas production by water displacement (apply Dalton's Law to subtract water vapor). Graph data, verify the laws, use PV=nRT to compute moles, and connect to KMT postulates.",
      },
    ],
    tools: [
      { label: "Worksheet Generator", path: "/worksheet" },
      { label: "Online Lab", path: "/lab" },
      { label: "Virtual Lab: Gas Laws", path: "/lab/virtual" },
    ],
  },
  {
    id: "solutions",
    number: 6,
    title: "Solutions & Solubility",
    teks: "§112.35(c)(12)",
    objective:
      "Investigate solution formation, solubility patterns, and factors affecting dissolution rates.",
    icon: Droplet,
    pathways: [
      {
        level: "Foundation",
        title: "Solubility Curves & Classification",
        description:
          "Analyze solubility curves for several salts; classify solutions as saturated/unsaturated/supersaturated; document temperature effects with graphs and infographics.",
      },
      {
        level: "Intermediate",
        title: "Solution Concentration & Dissolution Rates",
        description:
          "Run 3-4 experiments on solubility, the effects of temperature/agitation/particle size, and prepare solutions of known molarity. Document with comparative data displays.",
      },
    ],
    tools: [
      { label: "Solubility Table", path: "/tools/solubility" },
      { label: "Online Lab", path: "/lab" },
    ],
  },
  {
    id: "acid-base",
    number: 7,
    title: "Acid-Base Chemistry & pH",
    teks: "§112.35(c)(10)(I), (13)",
    objective:
      "Investigate acid-base chemistry, the pH scale, neutralization reactions, and quantitative titration.",
    icon: FlaskConical,
    pathways: [
      {
        level: "Foundation",
        title: "Acid-Base Classification & pH Study",
        description:
          "Cover Arrhenius and Bronsted-Lowry definitions, strong vs. weak, the pH scale, and household examples. Test pH of 10+ substances with paper or indicators and document results.",
      },
      {
        level: "Intermediate",
        title: "Titration & Equivalence Point",
        description:
          "Perform an acid-base titration (wet or virtual) using a known titrant and an indicator. Plot the titration curve, identify the equivalence point, and calculate the molarity of the unknown. Compare strong-strong vs. strong-weak curves and discuss buffer regions.",
      },
    ],
    tools: [
      { label: "Acid/Base pH Sort", path: "/play/ph-sort" },
      { label: "Online Lab (pH test)", path: "/lab" },
      { label: "Virtual Lab: Titration", path: "/lab/virtual" },
    ],
  },
  {
    id: "thermochemistry",
    number: 8,
    title: "Thermochemistry & Energy Transfer",
    teks: "§112.35(c)(14)(A)-(D)",
    objective:
      "Investigate energy changes in reactions through calorimetry, thermodynamics, Hess's Law, and standard enthalpies of formation.",
    icon: Flame,
    pathways: [
      {
        level: "Foundation",
        title: "Thermodynamics Concepts & Calculations",
        description:
          "Explain the laws of thermodynamics with everyday examples; classify reactions as exo/endothermic; draw thermochemical diagrams; solve q = mc(delta)T calculations; and use Hess's Law or standard enthalpies of formation to compute (delta)H for at least 3 target reactions.",
      },
      {
        level: "Intermediate",
        title: "Calorimetry Experimentation",
        description:
          "Run 3-4 calorimetry experiments (heat of combustion, dissolution, neutralization). Measure temperature change, calculate energy, write thermochemical equations, and verify one result using Hess's Law from tabulated (delta)Hf values.",
      },
    ],
    tools: [
      { label: "Online Lab", path: "/lab" },
      { label: "Equation Balancer", path: "/tools/equation-balancer" },
      { label: "Virtual Lab: Calorimetry", path: "/lab/virtual" },
    ],
  },
  {
    id: "nuclear",
    number: 9,
    title: "Nuclear Chemistry & Radioactivity",
    teks: "§112.35(c)(15)(A)-(D)",
    objective:
      "Explore nuclear reactions, radioactive decay, half-life calculations, and applications of nuclear phenomena.",
    icon: Radiation,
    pathways: [
      {
        level: "Foundation",
        title: "Nuclear Processes Study",
        description:
          "Present alpha, beta, and gamma decay; balance 12+ nuclear equations; solve 4-6 half-life problems (remaining mass, time elapsed, radiocarbon dating); distinguish fission and fusion; explore power, imaging, and therapy applications.",
      },
      {
        level: "Intermediate",
        title: "Nuclear Reactions & Applications",
        description:
          "Balance equations for varied decay/reactions, perform half-life calculations on real isotopes (I-131, C-14, U-238), build case studies on medicine/energy/dating, and discuss safety and waste with real data.",
      },
    ],
    tools: [
      { label: "Periodic Table", path: "/" },
      { label: "3D Atom Viewer", path: "/atom" },
    ],
  },
];

export interface Deliverable {
  title: string;
  description: string;
}

export const DELIVERABLES: Deliverable[] = [
  {
    title: "Scientific research",
    description:
      "Background research using credible sources (textbooks, peer-reviewed journals, scientific websites). Cite in APA or MLA.",
  },
  {
    title: "Visual component",
    description:
      "Posters, infographics, slides, video, diagrams, graphs, photos, 3D models, or concept maps - colorful, clearly labeled, scientifically accurate.",
  },
  {
    title: "Written component",
    description:
      "Overview, key concepts, methods, data, findings, conclusions, and real-world connections. Foundation 2-3 pp, Intermediate 3-4 pp, Advanced 4-5+ pp.",
  },
  {
    title: "Experimental component",
    description:
      "If your pathway includes labs: notebook with observations, data, photos/video, analysis, conclusions. Follow safety rules and get teacher approval.",
  },
  {
    title: "Live presentation",
    description:
      "Every pathway presents to the class. Foundation: 5-7 minutes, 10-15 slides or poster, plus Q&A.",
  },
];

export interface RubricItem {
  label: string;
  detail: string;
}

export const PRESENTATION_RUBRIC: RubricItem[] = [
  { label: "Time", detail: "5-7 minutes (Foundation)." },
  {
    label: "Format options",
    detail: "Slides (10-15), poster talk, or 3-4 minute video.",
  },
  {
    label: "Required content",
    detail:
      "Topic intro and importance, 3-4 key concepts, supporting visuals, real-world applications, clear conclusion, 3+ cited sources.",
  },
  {
    label: "Speaking",
    detail:
      "Clear pace, eye contact, visuals support (don't read), answer 2-3 audience questions.",
  },
];

export const TEACHER_RESOURCES: Record<string, TeacherResources> = {
  "lab-foundations": {
    lessonStarter:
      "Project an SDS for sodium hydroxide. Ask: 'You spill this on your hand - what do the next 60 seconds look like?'",
    instructions: [
      "Safety walk (10 min): Tour the lab; locate eyewash, shower, fire blanket, extinguisher, exits, and SDS binder.",
      "SDS literacy (15 min): Students read 2-3 SDS sheets and fill a hazard/PPE/first-aid table.",
      "Direct teach (15 min): SI units, unit conversion (factor-label), sig figs, accuracy vs. precision.",
      "Practice (10 min): Measurement worksheet with conversions and a graphing problem.",
      "Exit ticket (5 min): Students sign the safety contract and answer 5 quick safety scenarios.",
    ],
    toolNotes: [
      "Worksheet Generator - generate a sig-figs and unit-conversion problem set.",
      "Online Lab - use the measurement station to practice reading meniscus and balance to correct sig figs.",
    ],
  },
  "periodic-table": {
    lessonStarter:
      "Project the periodic table and ask: 'Which two elements would react most violently if you dropped them in water - and how can the table tell you?'",
    instructions: [
      "Warm-up (5 min): Class predicts trends across a period and down a group on the board.",
      "Guided exploration (20 min): Students open the Periodic Table tool and pick 4-6 elements from different families.",
      "Data capture (15 min): Students record symbol, atomic number, electron configuration, and one trend value (radius, EN, or IE).",
      "Share-out (10 min): Pairs present one trend they found and one anomaly.",
      "Exit ticket (5 min): Rank three elements by ionization energy and justify.",
    ],
    toolNotes: [
      "Periodic Table - main reference; show the property overlay if available.",
      "Periodic Sprint - use as a 3-minute warm-up to refresh symbol recall.",
      "Element of the Day - assign for homework streaks during the unit.",
    ],
  },
  "atomic-structure": {
    lessonStarter:
      "Show a flame test photo (Na, Cu, Li). Ask: 'Why does each element produce its own color?'",
    instructions: [
      "Hook (5 min): Flame-test photos and a quick poll on what produces the color.",
      "Direct teach (15 min): Walk the timeline Dalton -> Thomson -> Rutherford -> Bohr -> Schrodinger.",
      "Hands-on (20 min): Students use the 3D Atom Viewer to inspect 4-6 atoms/ions.",
      "Lab connection (10 min): Run a virtual flame test in the Online Lab; record colors and tie to transitions.",
      "Wrap-up (5 min): One-sentence summary linking electron transitions to emitted wavelengths.",
    ],
    toolNotes: [
      "3D Atom Viewer - assign specific atoms; have students screenshot configurations.",
      "Electron Config Sprint - 5-minute timed practice before formal assessment.",
      "Online Lab (flame tests) - lets every student safely 'run' tests.",
    ],
  },
  bonding: {
    lessonStarter:
      "Hold up table salt and a piece of copper wire. Ask: 'Why does one shatter and the other bend?'",
    instructions: [
      "Warm-up (5 min): Sort 6 substances into ionic / covalent / metallic on the board.",
      "Direct teach (15 min): Electron-dot structures, VSEPR basics, and a quick intro to intermolecular forces (London, dipole-dipole, hydrogen bonding).",
      "Practice (20 min): Students use Ion Engine to build 5 ionic compounds, then Molecule Viewer for 5 covalent ones.",
      "IMF mini-task (10 min): Rank H2O, H2S, CH4, NH3 by boiling point and justify.",
      "Exit ticket (5 min): Predict geometry, bond type, AND dominant IMF for 3 unseen formulas.",
    ],
    toolNotes: [
      "Ion Engine - drag cations/anions; great for visualizing charge balance.",
      "Bond Type Snap - rapid-fire formative assessment.",
      "Molecule Viewer - switch between 2D skeletal and 3D ball-and-stick to discuss geometry.",
    ],
  },
  stoichiometry: {
    lessonStarter:
      "Ask: 'If you burn 1 g of methane in a Bunsen burner, how much CO2 is in the air after?' Most students underestimate.",
    instructions: [
      "Warm-up (5 min): Convert 18 g of water to moles and to molecules.",
      "Direct teach (15 min): Mole road map - mass, moles, particles, volume.",
      "Practice (20 min): Students balance 5 equations with the Equation Balancer and compute molar masses.",
      "Application (10 min): Limiting-reactant problem with class data.",
      "Exit ticket (5 min): Percent yield of a small reaction.",
    ],
    toolNotes: [
      "Equation Balancer - use the steps view so students see balancing logic.",
      "Molar Mass Calculator - check work, not replace it.",
      "Reaction Race - end-of-class energizer.",
    ],
  },
  "gas-laws": {
    lessonStarter:
      "Crush an empty plastic bottle by adding hot water then capping it. Ask: 'Which gas law just happened?'",
    instructions: [
      "Demo (5 min): Hot/cold bottle crush demo or syringe pressure-volume.",
      "Direct teach (15 min): Boyle, Charles, Gay-Lussac, Combined, Ideal Gas Law (PV=nRT), and Dalton's Law of partial pressures.",
      "Calculation practice (20 min): Worksheet covering the simple laws plus 3-4 PV=nRT and 2 partial-pressure problems.",
      "Lab option (10 min): Gas-collection-by-water-displacement in the Online Lab; subtract water vapor pressure.",
      "Exit ticket (5 min): Identify which law applies to 4 scenarios and solve one PV=nRT.",
    ],
    toolNotes: [
      "Worksheet Generator - generate a custom problem set on gas laws.",
      "Online Lab - run a pressure-volume or volume-temperature experiment.",
    ],
  },
  solutions: {
    lessonStarter:
      "Stir sugar into iced tea and into hot tea side by side. Ask: 'Why the difference?'",
    instructions: [
      "Warm-up (5 min): Define solute, solvent, solution.",
      "Direct teach (15 min): Solubility curves; saturated/unsaturated/supersaturated.",
      "Tool work (20 min): Use the Solubility Table to predict precipitates from 5 mixing scenarios.",
      "Lab (10 min): Test temperature effect on dissolution rate in the Online Lab.",
      "Exit ticket (5 min): Read a solubility curve and classify a solution.",
    ],
    toolNotes: [
      "Solubility Table - quick lookup; pair with net ionic equations.",
      "Online Lab - controlled mixing without spillage.",
    ],
  },
  "acid-base": {
    lessonStarter:
      "Show pH paper dipped in lemon juice, water, and soap. Ask: 'What does pH actually measure?'",
    instructions: [
      "Hook (5 min): pH of 3 household items.",
      "Direct teach (15 min): Arrhenius vs. Bronsted-Lowry; strong vs. weak.",
      "Activity (20 min): Students play pH Sort and then test 10 substances in the Online Lab.",
      "Titration intro (10 min): Walk through one titration curve; find the equivalence point and compute molarity of the unknown.",
      "Exit ticket (5 min): Rank 4 solutions by pH and identify the equivalence point on a sample curve.",
    ],
    toolNotes: [
      "Acid/Base pH Sort - fast formative check on the pH scale.",
      "Online Lab (pH test) - virtual indicators with no chemical waste.",
      "Online Lab (titration) - run NaOH vs. HCl or NaOH vs. acetic acid and export the curve.",
    ],
  },
  thermochemistry: {
    lessonStarter:
      "Dissolve calcium chloride in water (or show a video). Ask: 'Where did the heat come from?'",
    instructions: [
      "Demo (5 min): Exothermic vs. endothermic dissolution.",
      "Direct teach (15 min): Energy diagrams, q = mc(delta)T, sign conventions, and Hess's Law with standard enthalpies of formation.",
      "Practice (20 min): Solve 3 calorimetry problems and 2 Hess's Law problems.",
      "Lab option (10 min): Heat of neutralization in the Online Lab; verify against tabulated (delta)Hf.",
      "Exit ticket (5 min): Label an energy diagram, identify the reaction type, and compute (delta)H for one target reaction using Hess's Law.",
    ],
    toolNotes: [
      "Online Lab - run calorimetry-style mixes and observe temperature shifts.",
      "Equation Balancer - balance thermochemical equations cleanly.",
    ],
  },
  nuclear: {
    lessonStarter:
      "Show a smoke detector and a banana side by side. Ask: 'Which is more radioactive?' (Both contain radioisotopes.)",
    instructions: [
      "Hook (5 min): Real-world sources of radiation slide.",
      "Direct teach (15 min): Alpha, beta, gamma decay; fission vs. fusion; half-life formula N = N0 * (1/2)^(t/T).",
      "Practice (20 min): Balance 6-8 nuclear equations AND solve 3-4 half-life problems (I-131, C-14, U-238).",
      "Application (10 min): Choose one of medicine / energy / radiocarbon dating and summarize using a half-life calculation.",
      "Exit ticket (5 min): Write the product of one alpha and one beta decay, and compute remaining mass after 3 half-lives.",
    ],
    toolNotes: [
      "Periodic Table - reference parent and daughter isotopes.",
      "3D Atom Viewer - visualize the nucleus before and after decay.",
    ],
  },
};
